import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Runtime feature flags stored in Firestore at appConfig/featureFlags.
 *
 * Unlike the build-time flags in src/constants/featureFlags.js, these can be
 * flipped without a redeploy — used to ship the AI report-card narrative code
 * "dark" and turn it on only after the Privacy Policy / DPA disclosure is
 * published. Reads fail CLOSED: any error or missing doc means the feature is
 * treated as OFF.
 *
 * Doc shape:
 *   appConfig/featureFlags {
 *     aiNarratives: boolean,            // general launch — visible to teachers + admins
 *     aiNarrativesAdminPreview: boolean // admin-only preview path (QA before launch)
 *     assessmentCsvExport: boolean      // gates the per-results-page CSV export panel
 *   }
 *
 * Intended launch sequence (aiNarratives):
 *   1. QA: set `aiNarrativesAdminPreview: true` (keep `aiNarratives: false`). Only
 *      admins see the feature, so they can test against real data before launch.
 *   2. General launch: set `aiNarrativesAdminPreview: false` and `aiNarratives: true`.
 *      All teachers + admins see it; the preview path is no longer needed.
 *
 * The two AI flags are independent booleans; the admin-preview path never weakens
 * any server-side check (auth, student assignment, rate limit all still apply).
 *
 * `assessmentCsvExport` gates the "Preview Export / Download CSV" panel rendered
 * on each results page (Letter/Sound, HFW, CVC, Nonsense Word Fluency). Off by
 * default — the feature is parked pending SIS-export work (see BACKLOG.md
 * "SIS assessment export"). Flip to true to restore the panel.
 */
export async function loadRemoteFeatureFlags() {
  try {
    const snap = await getDoc(doc(db, 'appConfig', 'featureFlags'));
    const data = snap.exists() ? snap.data() || {} : {};
    return {
      aiNarratives: data.aiNarratives === true,
      aiNarrativesAdminPreview: data.aiNarrativesAdminPreview === true,
      assessmentCsvExport: data.assessmentCsvExport === true,
    };
  } catch (err) {
    console.error('remoteFeatureFlags: load failed, treating all flags as OFF:', err);
    return {
      aiNarratives: false,
      aiNarrativesAdminPreview: false,
      assessmentCsvExport: false,
    };
  }
}

/** Convenience: resolve a single flag, default OFF. */
export async function isRemoteFeatureEnabled(flagName) {
  const flags = await loadRemoteFeatureFlags();
  return flags[flagName] === true;
}
